import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'student_subject_chapter_selection';

const SubjectChapterContext = createContext(null);

export function SubjectChapterProvider({ children }) {
  const initialSelection = (() => {
    try {
      if (typeof window === 'undefined') {
        return {
          selectedSubject: '',
          selectedChapter: '',
          selectedSubjectId: null,
          selectedChapterId: null,
        };
      }
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return {
          selectedSubject: '',
          selectedChapter: '',
          selectedSubjectId: null,
          selectedChapterId: null,
        };
      }
      const data = JSON.parse(stored);
      return {
        selectedSubject: data?.selectedSubject || '',
        selectedChapter: data?.selectedChapter || '',
        selectedSubjectId: data?.selectedSubjectId ?? null,
        selectedChapterId: data?.selectedChapterId ?? null,
      };
    } catch (error) {
      console.error('Error loading subject/chapter selection from localStorage:', error);
      return {
        selectedSubject: '',
        selectedChapter: '',
        selectedSubjectId: null,
        selectedChapterId: null,
      };
    }
  })();

  // Keep selection atomic to avoid state/persistence races.
  const [selection, setSelection] = useState(initialSelection);

  const isDebugEnabled = useCallback(() => {
    try {
      if (typeof window === 'undefined') return false;
      const v = window.localStorage.getItem('debug_subject_chapter_selection');
      return v === '1' || v === 'true' || v === 'yes';
    } catch {
      return false;
    }
  }, []);

  const debugLog = useCallback((event, payload) => {
    if (!isDebugEnabled()) return;
    try {
      const entry = {
        ts: new Date().toISOString(),
        event,
        payload,
        stack: new Error('selection update trace').stack,
      };

      if (typeof window !== 'undefined') {
        window.__subjectChapterSelectionDebug = window.__subjectChapterSelectionDebug || [];
        window.__subjectChapterSelectionDebug.push(entry);
        // keep last 25
        if (window.__subjectChapterSelectionDebug.length > 25) {
          window.__subjectChapterSelectionDebug.splice(0, window.__subjectChapterSelectionDebug.length - 25);
        }
      }

      // eslint-disable-next-line no-console
      console.log(`[SubjectChapter] ${event}`, payload);
      // eslint-disable-next-line no-console
      console.log(entry.stack);
    } catch {}
  }, [isDebugEnabled]);

  const persistSelection = useCallback((next) => {
    try {
      if (typeof window === 'undefined') return;

      const data = {
        selectedSubject: next.selectedSubject || '',
        selectedChapter: next.selectedChapter || '',
        selectedSubjectId: next.selectedSubjectId || null,
        selectedChapterId: next.selectedChapterId || null,
      };

      if (data.selectedSubjectId) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error saving subject/chapter selection to localStorage:', error);
    }
  }, []);

  // Clear selection (called on logout)
  const clearSelection = useCallback(() => {
    const next = {
      selectedSubject: '',
      selectedChapter: '',
      selectedSubjectId: null,
      selectedChapterId: null,
    };
    debugLog('clearSelection', { prev: selection, next });
    setSelection(next);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, [debugLog, selection]);

  // Update subject selection
  const updateSubject = useCallback((subjectId, subjectLabel) => {
    setSelection((prev) => {
      const next = {
        ...prev,
        selectedSubject: subjectLabel || '',
        selectedSubjectId: subjectId || null,
        // Clear chapter when subject changes
        selectedChapter: '',
        selectedChapterId: null,
      };
      debugLog('updateSubject', { args: { subjectId, subjectLabel }, prev, next });
      persistSelection(next);
      return next;
    });
  }, [persistSelection]);

  // Update chapter selection
  const updateChapter = useCallback((chapterId, chapterLabel) => {
    setSelection((prev) => {
      const next = {
        ...prev,
        selectedChapter: chapterLabel || '',
        selectedChapterId: chapterId || null,
      };
      debugLog('updateChapter', { args: { chapterId, chapterLabel }, prev, next });
      persistSelection(next);
      return next;
    });
  }, [persistSelection]);

  // Update both subject and chapter
  const updateSelection = useCallback((subjectId, subjectLabel, chapterId, chapterLabel) => {
    setSelection((prev) => {
      const next = {
        ...prev,
        selectedSubject: subjectLabel || '',
        selectedSubjectId: subjectId || null,
        selectedChapter: chapterLabel || '',
        selectedChapterId: chapterId || null,
      };
      debugLog('updateSelection', { args: { subjectId, subjectLabel, chapterId, chapterLabel }, prev, next });
      persistSelection(next);
      return next;
    });
  }, [persistSelection]);

  const selectedSubject = selection.selectedSubject;
  const selectedChapter = selection.selectedChapter;
  const selectedSubjectId = selection.selectedSubjectId;
  const selectedChapterId = selection.selectedChapterId;

  const value = {
    selectedSubject,
    selectedChapter,
    selectedSubjectId,
    selectedChapterId,
    updateSubject,
    updateChapter,
    updateSelection,
    clearSelection,
  };

  return (
    <SubjectChapterContext.Provider value={value}>
      {children}
    </SubjectChapterContext.Provider>
  );
}

export function useSubjectChapter() {
  const context = useContext(SubjectChapterContext);
  if (!context) {
    throw new Error('useSubjectChapter must be used within SubjectChapterProvider');
  }
  return context;
}

// Helper function to clear selection on logout (can be called from auth utils)
export function clearSubjectChapterSelection() {
  localStorage.removeItem(STORAGE_KEY);
}

